import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { PropertyValues } from 'lit';
import { dispatch } from '../../utils/event-helpers';

/**
 * A toast-style notification message for real-time feedback on user actions,
 * system events, and background task completion.
 *
 * Renders an optional icon, a required title, a required description, an
 * optional progress indicator, and optional inline actions. A built-in close
 * button allows the user to dismiss the notification. The component uses CSS
 * Container Queries to adapt between desktop and mobile layouts based on its
 * own inline size rather than the viewport.
 *
 * @element asc-notification
 *
 * @fires asc-dismiss - Fired when the user clicks the close button or presses Escape while focus is within the notification.
 *
 * @slot icon - Optional leading icon displayed to the left of the title and description. Accepts any `<svg>` or `<asc-icon>`.
 * @slot title - **Required.** The bold heading text of the notification.
 * @slot - **Required.** The secondary body text (description) displayed beneath the title.
 * @slot progress - Optional progress bar rendered between the description and the actions area.
 * @slot actions - Optional inline action elements rendered beneath the description or progress bar.
 *
 * @csspart base - The root `<output>` wrapper for the entire notification container.
 * @csspart icon-container - The `<span>` wrapping the icon slot. Collapses when empty.
 * @csspart content - The `<section>` wrapping title, description, progress, and actions.
 * @csspart header - The `<header>` wrapping the title slot.
 * @csspart progress - The `<div>` wrapping the progress slot.
 * @csspart actions - The `<footer>` wrapping the actions slot.
 * @csspart close-button - The built-in close/dismiss `<button>`.
 *
 * @cssprop --asc-notification-bg - Background color. Default: `var(--asc-bg-primary_alt)`
 * @cssprop --asc-notification-border-color - Border color. Default: `var(--asc-border-secondary)`
 * @cssprop --asc-notification-border-width - Border width. Default: `var(--asc-border-width)`
 * @cssprop --asc-notification-border-radius - Border radius. Default: `var(--asc-radius-xl)`
 * @cssprop --asc-notification-shadow - Box shadow. Default: 3-layer lg shadow
 * @cssprop --asc-notification-padding - Padding. Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-notification-icon-gap - Gap between icon and content. Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-notification-icon-size - Icon container size. Default: `var(--asc-spacing-5xl)`
 * @cssprop --asc-notification-icon-color - Icon color. Default: `var(--asc-fg-secondary)`
 * @cssprop --asc-notification-title-font-size - Title font size. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-notification-title-font-weight - Title font weight. Default: `var(--asc-font-weight-semibold)`
 * @cssprop --asc-notification-title-color - Title text color. Default: `var(--asc-fg-primary)`
 * @cssprop --asc-notification-title-line-height - Title line height. Default: `var(--asc-line-height-text-sm)`
 * @cssprop --asc-notification-description-font-size - Description font size. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-notification-description-color - Description text color. Default: `var(--asc-fg-secondary)`
 * @cssprop --asc-notification-description-line-height - Description line height. Default: `var(--asc-line-height-text-sm)`
 * @cssprop --asc-notification-content-gap - Gap between title and description. Default: `var(--asc-spacing-xs)`
 * @cssprop --asc-notification-progress-gap - Gap between description and progress. Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-notification-actions-gap - Gap between progress/description and actions. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-notification-close-size - Close button size. Default: `36px`
 * @cssprop --asc-notification-close-padding - Close button padding. Default: `var(--asc-spacing-md)`
 * @cssprop --asc-notification-close-radius - Close button border radius. Default: `var(--asc-radius-md)`
 * @cssprop --asc-notification-close-color - Close button icon color. Default: `var(--asc-fg-quaternary)`
 * @cssprop --asc-notification-close-color-hover - Close button hover color. Default: `var(--asc-fg-quaternary_hover)`
 * @cssprop --asc-notification-focus-ring - Focus ring color. Default: `var(--asc-focus-ring)`
 * @cssprop --asc-notification-transition - Transition timing. Default: `var(--asc-transition-base)`
 */
@customElement('asc-notification')
export class AscNotification extends LitElement {
  static override styles = css`
    :host {
      display: block;
      container-type: inline-size;
      container-name: notification;
    }

    [part='base'] {
      display: flex;
      align-items: flex-start;
      position: relative;
      overflow: clip;
      padding: var(--asc-notification-padding, var(--asc-spacing-xl));
      background: var(--asc-notification-bg, var(--asc-bg-primary_alt));
      border: var(--asc-notification-border-width, var(--asc-border-width)) solid
        var(--asc-notification-border-color, var(--asc-border-secondary));
      border-radius: var(--asc-notification-border-radius, var(--asc-radius-xl));
      box-shadow: var(
        --asc-notification-shadow,
        0 12px 16px -4px var(--asc-shadow-lg_01),
        0 4px 6px -2px var(--asc-shadow-lg_02),
        0 2px 2px -1px var(--asc-shadow-lg_03)
      );
      gap: var(--asc-notification-icon-gap, var(--asc-spacing-xl));
      font-family: var(--asc-font-family-body);
    }

    [part='icon-container'] {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: var(--asc-notification-icon-size, var(--asc-spacing-5xl));
      height: var(--asc-notification-icon-size, var(--asc-spacing-5xl));
      color: var(--asc-notification-icon-color, var(--asc-fg-secondary));
    }

    :host(:not([data-has-icon])) [part='icon-container'] {
      display: none;
    }

    [part='content'] {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      padding-right: var(--asc-spacing-4xl);
    }

    :host([data-has-icon]) [part='content'] {
      padding-top: var(--asc-spacing-xxs);
    }

    [part='header'] ::slotted(*) {
      font-size: var(--asc-notification-title-font-size, var(--asc-font-size-text-sm));
      font-weight: var(--asc-notification-title-font-weight, var(--asc-font-weight-semibold));
      color: var(--asc-notification-title-color, var(--asc-fg-primary));
      line-height: var(--asc-notification-title-line-height, var(--asc-line-height-text-sm));
    }

    ::slotted(:not([slot])) {
      font-size: var(--asc-notification-description-font-size, var(--asc-font-size-text-sm));
      color: var(--asc-notification-description-color, var(--asc-fg-secondary));
      line-height: var(--asc-notification-description-line-height, var(--asc-line-height-text-sm));
      margin: 0;
      margin-top: var(--asc-notification-content-gap, var(--asc-spacing-xs));
    }

    [part='progress'] {
      margin-top: var(--asc-notification-progress-gap, var(--asc-spacing-xl));
    }

    :host(:not([data-has-progress])) [part='progress'] {
      display: none;
    }

    [part='actions'] {
      margin-top: var(--asc-notification-actions-gap, var(--asc-spacing-lg));
    }

    :host(:not([data-has-actions])) [part='actions'] {
      display: none;
    }

    [part='close-button'] {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      top: var(--asc-spacing-md);
      right: var(--asc-spacing-md);
      width: var(--asc-notification-close-size, 36px);
      height: var(--asc-notification-close-size, 36px);
      padding: var(--asc-notification-close-padding, var(--asc-spacing-md));
      border: none;
      background: none;
      color: var(--asc-notification-close-color, var(--asc-fg-quaternary));
      cursor: pointer;
      border-radius: var(--asc-notification-close-radius, var(--asc-radius-md));
      transition: color var(--asc-notification-transition, var(--asc-transition-base));
    }

    [part='close-button']:hover {
      color: var(--asc-notification-close-color-hover, var(--asc-fg-quaternary_hover));
    }

    [part='close-button']:focus-visible {
      outline: 2px solid var(--asc-notification-focus-ring, var(--asc-focus-ring));
      outline-offset: 2px;
    }

    [part='close-button'] svg {
      width: 20px;
      height: 20px;
    }

    /* ─── Mobile layout ─── */
    @container notification (max-width: 375px) {
      [part='base'] {
        display: grid;
        grid-template-columns: auto 1fr auto;
        grid-template-rows: auto 1fr;
        grid-template-areas:
          'icon  .       close'
          'content content content';
        padding: var(--asc-notification-padding, var(--asc-spacing-xl));
        gap: var(--asc-spacing-xl);
      }

      [part='icon-container'] {
        grid-area: icon;
      }

      [part='content'] {
        grid-area: content;
        padding-right: 0;
      }

      [part='close-button'] {
        position: static;
        grid-area: close;
      }
    }
  `;

  /** Current progress percentage (0–100). Only relevant when the progress slot is populated. */
  @property({ type: Number }) progress = 0;

  /** Accessible label for the progress bar. Defaults to "Progress". */
  @property({ attribute: 'progress-label' }) progressLabel = 'Progress';

  @state() private _hasIcon = false;
  @state() private _hasProgress = false;
  @state() private _hasActions = false;

  override willUpdate(changed: PropertyValues): void {
    if (changed.has('_hasIcon')) {
      if (this._hasIcon) {
        this.setAttribute('data-has-icon', '');
      } else {
        this.removeAttribute('data-has-icon');
      }
    }
    if (changed.has('_hasProgress')) {
      if (this._hasProgress) {
        this.setAttribute('data-has-progress', '');
      } else {
        this.removeAttribute('data-has-progress');
      }
    }
    if (changed.has('_hasActions')) {
      if (this._hasActions) {
        this.setAttribute('data-has-actions', '');
      } else {
        this.removeAttribute('data-has-actions');
      }
    }
  }

  override firstUpdated(): void {
    this._initSlotState('icon');
    this._initSlotState('progress');
    this._initSlotState('actions');
  }

  private _initSlotState(slotName: string): void {
    const slot = this.shadowRoot?.querySelector<HTMLSlotElement>(
      `slot[name="${slotName}"]`,
    );
    const hasContent = (slot?.assignedNodes({ flatten: true }).length ?? 0) > 0;
    switch (slotName) {
      case 'icon':
        this._hasIcon = hasContent;
        break;
      case 'progress':
        this._hasProgress = hasContent;
        break;
      case 'actions':
        this._hasActions = hasContent;
        break;
    }
  }

  private _handleSlotChange(slotName: string): void {
    const slot = this.shadowRoot?.querySelector<HTMLSlotElement>(
      `slot[name="${slotName}"]`,
    );
    const hasContent = (slot?.assignedNodes({ flatten: true }).length ?? 0) > 0;

    switch (slotName) {
      case 'icon':
        this._hasIcon = hasContent;
        break;
      case 'progress':
        this._hasProgress = hasContent;
        break;
      case 'actions':
        this._hasActions = hasContent;
        break;
    }
  }

  private _handleDismiss(): void {
    dispatch(this, 'asc-dismiss');
  }

  private _handleKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      dispatch(this, 'asc-dismiss');
    }
  }

  override render() {
    const clampedProgress = Math.min(100, Math.max(0, this.progress));

    return html`
      <output
        part="base"
        role="status"
        @keydown=${this._handleKeyDown}
      >
        <span part="icon-container">
          <slot name="icon" @slotchange=${() => this._handleSlotChange('icon')}></slot>
        </span>
        <section part="content">
          <header part="header">
            <slot name="title"></slot>
          </header>
          <slot></slot>
          <div
            part="progress"
            role=${this._hasProgress ? 'progressbar' : nothing}
            aria-label=${this._hasProgress ? this.progressLabel : nothing}
            aria-valuenow=${this._hasProgress ? clampedProgress : nothing}
            aria-valuemin=${this._hasProgress ? 0 : nothing}
            aria-valuemax=${this._hasProgress ? 100 : nothing}
          >
            <slot name="progress" @slotchange=${() => this._handleSlotChange('progress')}></slot>
          </div>
          <footer part="actions">
            <slot name="actions" @slotchange=${() => this._handleSlotChange('actions')}></slot>
          </footer>
        </section>
        <button
          part="close-button"
          aria-label="Dismiss notification"
          type="button"
          @click=${this._handleDismiss}
        >
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </output>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-notification': AscNotification;
  }
}
